import { GoTrueClient } from '@supabase/auth-js'
import { Capacitor } from '@capacitor/core'
import { GoogleAuth } from '@southdevs/capacitor-google-auth'
import { markSignoutSelfInitiated } from './authDiag'
import { durableSessionStorage } from './durableSessionStorage'

// Resolve the GoTrue URL with a tiered fallback. Build-time env var wins;
// otherwise, on a native (Capacitor) build we MUST default to the
// production GoTrue rather than localhost — the device can't reach
// http://localhost:9999, so a missing env var on iOS used to surface as
// "Network Error" on the login screen with no further diagnostic.
// (This was the TestFlight-build-13 sign-in bug — `apps/web/.env.production`
// wasn't committed, the Fastfile build produced a bundle with no
// VITE_GOTRUE_URL, and the original `?? 'http://localhost:9999'` fallback
// shipped to every phone.) On the web we still default to localhost
// because that's actually where dev developers run GoTrue.
function resolveGoTrueUrl(): string {
  const fromEnv = import.meta.env.VITE_GOTRUE_URL
  if (fromEnv) return fromEnv
  if (Capacitor.isNativePlatform()) return 'https://auth.trybeup.com'
  return 'http://localhost:9999'
}

// No-op lock for GoTrueClient. The library's default `navigatorLock`
// uses `navigator.locks.request` with a hardcoded 5000ms acquire
// timeout (see node_modules/@supabase/auth-js/dist/main/GoTrueClient.js
// line 28: `lockAcquireTimeout: 5000`). On a cold WKWebView start the
// LockManager state is uninitialised and the very first request times
// out deterministically — which we observed as a flat ~5024ms in the
// react_render→session_ready segment of the founder's boot-timing chip,
// repeated to the millisecond across multiple cold opens (1.1.215 +
// 1.1.216). The library exports `lockNoOp` for exactly this case; the
// lock exists to coordinate token refreshes across BROWSER TABS, and
// inside Capacitor's single WebView there is no second tab to
// coordinate with — locking is pure overhead. Web users keep the
// default lock so cross-tab safety isn't sacrificed for them. Founder
// cold-start fix, 2026-06-14.
const noOpLock = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => fn()

export const authClient = new GoTrueClient({
  url: resolveGoTrueUrl(),
  storageKey: 'trybeup_session',
  // Hybrid localStorage + IndexedDB adapter. Survives iOS Safari's
  // memory-pressure eviction and ITP cleanup of localStorage, which
  // was the recurring root cause of the "Safari mobile signed me out
  // again" reports after the warmer retry + provider-gate + idle
  // heartbeat fixes were already in place. See
  // apps/web/src/lib/durableSessionStorage.ts for the full rationale.
  storage: durableSessionStorage,
  // Native-only — see noOpLock comment above. Web keeps the default
  // cross-tab navigator-locks behaviour.
  ...(Capacitor.isNativePlatform() ? { lock: noOpLock } : {}),
  // Disabled deliberately. With autoRefreshToken: true, the library runs an
  // internal timer that refreshes the access token every ~1h. If THAT call
  // fails with anything that isn't a retryable fetch error (i.e. anything
  // other than a network drop or 5xx), the library calls its private
  // `_removeSession()` and emits `SIGNED_OUT` — silently logging the user
  // out. Common triggers: a 400 invalid_grant during a refresh-token
  // rotation race between tabs, a brief 4xx during a deploy window, etc.
  //
  // Our axios interceptor in lib/api.ts already refreshes on 401 with a
  // 5xx-tolerant outcome (see `tryRefreshSession`), so we don't need the
  // library's timer. Refreshing only when an API call actually returns 401
  // is sufficient and removes the silent-signout vector entirely.
  autoRefreshToken: false,
  persistSession: true,
  detectSessionInUrl: true,
})

export async function getAccessToken(): Promise<string | null> {
  const { data } = await authClient.getSession()
  return data.session?.access_token ?? null
}

export async function signOut(reason: string = 'user-initiated') {
  // Mark this sign-out as "we did this on purpose" so the SIGNED_OUT
  // auth-state-change listener can tell expected sign-outs apart from
  // ones the SDK fires internally after a failed refresh — that
  // distinction is exactly what the Safari mobile sign-out diagnostics
  // need.
  markSignoutSelfInitiated(reason)

  // Clear per-user localStorage so a new user on the same device gets a clean
  // slate (e.g. onboarding state from the previous account).
  localStorage.removeItem('trybeup_onboarding_complete')
  localStorage.removeItem('trybeup_onboarding_user')
  localStorage.removeItem('trybeup_session_temporary')
  localStorage.removeItem('trybeup_last_active')
  // Added 2026-06-12 with the coach-conversation persistence shipped
  // in 1.1.175 — without this, a shared device that signs out and back
  // in as a different user would land the new account on the previous
  // user's conversation ID. The messages query 404/403 recovery in
  // MyCoach handles the next step gracefully, but clearing here means
  // the new user never sees the broken-chat error state at all.
  localStorage.removeItem('trybeup_coach_active_conv')
  // Added 2026-06-20 with the Focus Mode feature. Both records are
  // per-user (focus session contains the previous account's habit
  // id; pending-capgo-apply is a per-device update marker but
  // dropping it on sign-out is harmless — it just defers the post-
  // launch '✓ Updated' toast for one cycle). Without these, a
  // family-shared device that signs out and back in as a different
  // user could restore the previous account's prayer timer and try
  // to log it against the new user → 404 'Couldn't log the session'.
  localStorage.removeItem('trybeup_focus_session')
  localStorage.removeItem('trybeup_capgo_pending_apply')
  // Cached premium flag (useSubscription's first-paint fallback). Without this,
  // a free user signing in after a premium user on a shared device sees
  // premium-only UI until their /api/subscription fetch settles — a confusing
  // flash and a mild leak of the prior user's status. Every real gate is
  // server-side so it's cosmetic, but clear it anyway (audit 2026-07-19).
  localStorage.removeItem('trybeup:isPremium')

  // On native iOS / Android we also need to clear the Google Sign-In SDK's
  // cached account, otherwise the next "Continue with Google" tap silently
  // re-uses the previously signed-in Google account without showing the
  // account picker — which prevents switching between Google accounts.
  // Best-effort: never let a Google SDK error block GoTrue sign-out.
  if (Capacitor.isNativePlatform()) {
    try {
      await GoogleAuth.signOut()
    } catch {
      // ignore — user may not have signed in via Google on this device
    }
  }

  await authClient.signOut()
}
